import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/modal'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Icon,
  Input,
  Link,
  Text,
} from '@chakra-ui/react'
import type { FieldProps } from 'formik'
import { Field, Formik } from 'formik'
import React, { useContext, useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { CgRename } from 'react-icons/cg'
import useFetch, { CachePolicies } from 'use-http'
import * as Yup from 'yup'
import { colors } from '../../../../ui-kit'
import useToast from '../../../hooks/useToast'
import type {
  FileDetailsResponse,
  MediaLibrarySearchFileResponse,
  RenameFileErrorResponse,
  RenameFilePartialSuccessResponse,
} from '../../../interfaces'
import { MLContext } from '../../../providers'
import { MediaLibraryActions } from '../../../providers/MLProvider'

interface Props {
  fileDetails?: MediaLibrarySearchFileResponse | FileDetailsResponse
  setFileDetails?: React.Dispatch<
    React.SetStateAction<FileDetailsResponse | undefined>
  >
  isOpen: boolean
  onClose(): void
}

interface FormProps {
  newFileName: string
  purgeCache: boolean
}

const RenameFileModal = ({
  fileDetails,
  setFileDetails,
  isOpen,
  onClose,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const toast = useToast()
  const [selectedFile, setSelectedFile] = useState<
    MediaLibrarySearchFileResponse | FileDetailsResponse | undefined
  >()
  const [formError, setFormError] = useState<string>('')
  const { response, request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })
  const [{ selectedFiles }, dispatch] = useContext(MLContext)

  useEffect(() => {
    setSelectedFile(fileDetails ?? selectedFiles[0])
  }, [isOpen, fileDetails, selectedFiles[0]?.name])

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose()
        setFormError('')
      }}
      size="lg"
      returnFocusOnClose={false}
    >
      <ModalOverlay />
      <ModalContent>
        <Formik<FormProps>
          enableReinitialize
          initialValues={{
            newFileName: selectedFile?.name ?? '',
            purgeCache: false,
          }}
          onSubmit={async (values): Promise<void> => {
            setFormError('')
            const res = (await request.put('/api/v2/files/rename/', {
              filePath: selectedFile?.filePath,
              newFileName: values.newFileName,
              purgeCache: values.purgeCache,
            })) as
              | RenameFileErrorResponse
              | MediaLibrarySearchFileResponse
              | RenameFilePartialSuccessResponse

            if (request.error) {
              if ([400, 404, 409].includes(response.status)) {
                if ((res as RenameFileErrorResponse).message) {
                   setFormError((res as RenameFileErrorResponse).message)
                } else {
                  setFormError( t('invalid_new_file_name', 'Invalid new file name.') )
                }
              } else {
                setFormError(
                  t(
                    'error_in_renaming_file',
                    'Error in renaming file, please try again after sometime',
                  ),
                );
              }
            } else if (response.status === 207) {
              toast({
                description: (res as RenameFilePartialSuccessResponse).message,
                status: 'info',
                isClosable: true,
                duration: 5000,
                position: 'top-right',
                variant: 'outline',
              })
              setFormError('')
              onClose()
              dispatch({
                type: MediaLibraryActions.UPDATE_FILES,
                payload: [{ ...selectedFile?.[0], name: values.newFileName }],
              })
              if (
                selectedFiles[0]?.name === fileDetails?.name ||
                !fileDetails
              ) {
                dispatch({
                  type: MediaLibraryActions.SET_SELECTED_FILES,
                  payload: [{ ...selectedFile?.[0], name: values.newFileName }],
                })
              }
              dispatch({
                type: MediaLibraryActions.UPDATE_FILES,
                payload: [{ ...selectedFile?.[0], name: values.newFileName }],
              })
              setFileDetails?.({
                ...selectedFile?.[0],
                name: values.newFileName,
              })
            } else {
              toast({
                description: t(
                  'file_renamed_successfully',
                  'File renamed successfully',
                ),
                status: 'success',
                isClosable: true,
                duration: 5000,
                position: 'top-right',
                variant: 'outline',
              })
              setFormError('')
              const updated = res as MediaLibrarySearchFileResponse

              if (
                selectedFiles[0]?.name === fileDetails?.name ||
                !fileDetails
              ) {
                dispatch({
                  type: MediaLibraryActions.SET_SELECTED_FILES,
                  payload: [updated],
                })
              }
              dispatch({
                type: MediaLibraryActions.UPDATE_FILES,
                payload: [updated],
              })
              setFileDetails?.(updated as unknown as FileDetailsResponse)
              onClose()
            }
          }}
          validationSchema={Yup.object().shape({
            newFileName: Yup.string().required('Required'),
            purgeCache: Yup.boolean().required('Required'),
          })}
        >
          {({ values, handleChange, isSubmitting, submitForm }) => (
            <>
              <ModalHeader>
                <Icon
                  as={CgRename}
                  color="appBattleshipGrey"
                  backgroundColor="appPaleGrey"
                  boxSize={6}
                  padding="0.1em"
                  borderRadius="full"
                  marginRight="0.5em"
                />
                {t('rename_file', 'Rename File')}
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <Alert status="info">
                  <AlertIcon />
                  <AlertDescription>
                    <Trans t={t} i18nKey="rename_file_purge_cache_alert">
                      <Text lineHeight="1.2em">
                        Purge requests are counted against account's
                      </Text>{' '}
                      <Link
                        lineHeight="1.2em"
                        href="https://docs.imagekit.io/api-reference/media-api/purge-cache#limitations"
                        isExternal
                      >
                        purge API limits
                      </Link>
                    </Trans>
                  </AlertDescription>
                </Alert>
                {formError ? (
                  <Alert status="error" mt="1em">
                    <AlertIcon />
                    <AlertDescription>{formError}</AlertDescription>
                  </Alert>
                ) : null}
                <Field name="newFileName">
                  {({ field, form }: FieldProps<string, FormProps>) => (
                    <FormControl
                      marginTop="1em"
                      isInvalid={Boolean(
                        form.errors.newFileName && form.touched.newFileName,
                      )}
                      isRequired
                    >
                      <FormLabel>
                        {t('new_file_name', 'New File Name')}
                      </FormLabel>
                      <Trans t={t} i18nKey="rename_file_field_description">
                        <Text color="appBattleshipGrey" marginBottom="0.5em">
                          Enter a valid file name.{' '}
                          <Link
                            href="https://docs.imagekit.io/api-reference/media-api/rename-file"
                            lineHeight="1.15em"
                            isExternal
                          >
                            Learn More.
                          </Link>
                        </Text>
                      </Trans>
                      <Input {...field} placeholder={selectedFile?.name} />
                      {form.errors.newFileName && form.touched.newFileName ? (
                        <FormErrorMessage mt="0.25em">
                          {form.errors.newFileName}
                        </FormErrorMessage>
                      ) : (
                        <Box height="1.55em" />
                      )}
                    </FormControl>
                  )}
                </Field>
                <Field name="purgeCache">
                  {({ field, form }: FieldProps<string, FormProps>) => (
                    <FormControl
                      marginTop="0.5em"
                      isInvalid={Boolean(
                        form.errors.purgeCache && form.touched.purgeCache,
                      )}
                      isRequired
                    >
                      <Checkbox
                        {...field}
                        id="purgeCache"
                        isInvalid={Boolean(
                          form.errors.purgeCache && form.touched.purgeCache,
                        )}
                        isChecked={values.purgeCache}
                        onChange={handleChange}
                      >
                        {t(
                          'purge_cache_checkbox_label',
                          'Purge CDN cache for old URL',
                        )}
                      </Checkbox>
                      {form.errors.purgeCache && form.touched.purgeCache ? (
                        <FormErrorMessage mt="0.25em">
                          {form.errors.purgeCache}
                        </FormErrorMessage>
                      ) : (
                        <Box height="1.55em" />
                      )}
                    </FormControl>
                  )}
                </Field>
              </ModalBody>
              <ModalFooter
                borderTop={`1px solid ${colors.appGrey}`}
                style={{ gap: '1em' }}
              >
                <Button
                  onClick={() => {
                    onClose()
                    setFormError('')
                  }}
                  variant="outline"
                >
                  {t('cancel', 'Cancel')}
                </Button>
                <Button
                  type="submit"
                  isDisabled={isSubmitting}
                  isLoading={isSubmitting}
                  onClick={async () => {
                    await submitForm()
                  }}
                >
                  {t('submit', 'Submit')}
                </Button>
              </ModalFooter>
            </>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  )
}

export default RenameFileModal
